import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Bounce, toast } from 'react-toastify';
import ProjectMyTasksTable from '../../../components/DashboardComponents/ProjectMyTasksTable';
import {
  CreateTaskModal,
  JoinShadowSessionModal,
  StartShadowSessionModal,
  StartSoloSessionModal,
} from '../../../components/Modals';
import Pagination from '../../../components/Listing/Pagination';
import { ICONS } from '../../../constants/icons';
import { get, patch } from '../../../api/axios';
import endpoints from '../../../api/endpoints';
import { getApiErrorMessage } from '../../../helpers/apiError';
import { getUserAvatarUrl } from '../../../helpers/userAvatar';
import { getUserFullName } from '../../../helpers/userDisplay';
import { formatWorkedHours } from '../../../helpers/sessionDetail';
import {
  createProjectSession,
  getProjectCollaborators,
  joinProjectSession,
  resolveTaskSessionAction,
  taskMatchesOngoingSession,
  withTaskOngoingSession,
} from '../../../helpers/session';
import { listingReturnToFromPath } from '../../../helpers/sessionReturnTo';
import { useSessionDetailPlayer } from '../../../components/FloatingPlayer';

const PAGE_SIZE = 10;
const TASK_INCLUDES = ['collaborators.user', 'creator'];

const markTaskSessionJoined = (task, sessionId) =>
  withTaskOngoingSession(task, {
    sessionId,
    sessionStatus: 'active',
    userStatus: 'joined',
  });

const clearTaskSessionIfMatches = (task, leftSessionId) =>
  taskMatchesOngoingSession(task, leftSessionId)
    ? withTaskOngoingSession(task)
    : task;

const taskListPath = (projectId, memberId) =>
  `/projects/${projectId}/tasks/${memberId}`;

const taskDetailPath = (projectId, memberId, taskId) =>
  `/projects/${projectId}/tasks/${memberId}/detail/${taskId}`;

const unwrapTaskPayload = (payload) => {
  if (!payload) return null;
  if (payload.id) return payload;
  if (payload.data?.id) return payload.data;
  if (Array.isArray(payload.data)) {
    return payload.data[0] || null;
  }
  return null;
};

const parseDateOnly = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const formatDate = (value) => {
  const date = parseDateOnly(value);
  return date
    ? date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      })
    : '—';
};

const STATUS_LABEL_BY_API = {
  in_progress: 'In Progress',
  completed: 'Completed',
  pending: 'Not Started',
  not_started: 'Not Started',
};

const STATUS_API_BY_LABEL = {
  'In Progress': 'in_progress',
  Completed: 'completed',
  'Not Started': 'pending',
};

const mapApiStatusLabel = (value) => {
  if (!value) return null;
  const key = String(value).toLowerCase().replace(/[\s-]+/g, '_');
  return STATUS_LABEL_BY_API[key] || null;
};

const deriveStatus = (task) => {
  const fromApi = mapApiStatusLabel(task.status);
  if (fromApi) return fromApi;

  const today = startOfToday();
  const start = parseDateOnly(task.start_date);
  const end = parseDateOnly(task.end_date);

  if (end && end < today) return 'Completed';
  if (start && start > today) return 'Not Started';
  if (start) return 'In Progress';
  return 'Not Started';
};

const applyTaskStatus = (task, label) => {
  const completed = label === 'Completed';
  const sessionAction = resolveTaskSessionAction(task);
  return {
    ...task,
    status: label,
    completed,
    sessionAction: completed ? 'done' : sessionAction,
    action: completed ? 'done' : sessionAction === 'start' ? 'start' : 'continue',
  };
};

const EMPTY_TASK_STATS = {
  total: 0,
  completed: 0,
  inProgress: 0,
  notStarted: 0,
  hoursLogged: '0h 0m 0s',
};

const formatLoggedHours = (value) => {
  if (value == null || value === '') return '—';
  return formatWorkedHours(value);
};

const formatEstimatedHours = (value) => {
  if (value == null || value === '') return '—';
  return formatWorkedHours(value);
};

const displayMemberRole = (role) => {
  if (!role) return 'Member';
  if (String(role).toLowerCase() === 'member') return 'Member';
  return role;
};

const userDisplayName = (user) => getUserFullName(user);

const memberUserId = (item) =>
  String(item?.userId || item?.user_id || item?.user?.id || item?.owner?.id || '');

const findMemberIdForUser = (project, userId) => {
  if (!userId) return '';
  const members = [
    ...(project?.members || []),
    ...(project?.collaborators || []),
    ...(project?.estimatedHours || []),
  ];
  const member = members.find((item) => memberUserId(item) === String(userId));
  return member?.id ? String(member.id) : '';
};

const mapMemberDetailToOwner = (member, currentUserId) => {
  if (!member) return null;
  const user = member.user || {};
  const userId = String(member.user_id || user.id || '');
  const isYou = Boolean(currentUserId && userId === String(currentUserId));
  const role =
    member.role_types?.[0]?.public_name ||
    member.role_types?.[0]?.name ||
    displayMemberRole(member.role);

  return {
    id: userId,
    memberId: member.id,
    name: userDisplayName(user) || 'Member',
    avatar: getUserAvatarUrl(user),
    role,
    roleTone: 'purple',
    isYou,
  };
};

const collaboratorDisplayName = (collaborator) =>
  getUserFullName(collaborator?.user || collaborator) || null;

const mapCollaborators = (task) =>
  (Array.isArray(task.collaborators) ? task.collaborators : [])
    .map((collaborator) => ({
      id: collaborator.id || collaborator.user?.id,
      userId: collaborator.user_id || collaborator.user?.id || '',
      email: collaborator.user?.email || collaborator.email || '',
      name: collaboratorDisplayName(collaborator),
      src: getUserAvatarUrl(collaborator?.user),
    }))
    .filter((person) => person.name || person.src);

const mapTaskOwner = (task, currentUserId) => {
  const creator = task.creator || task.user || {};
  const creatorId = creator.id != null ? String(creator.id) : '';
  const isYou = Boolean(currentUserId && creatorId === String(currentUserId));
  const name =
    getUserFullName(creator) ||
    (isYou ? 'You' : 'Member');

  return {
    id: creatorId,
    name,
    avatar: getUserAvatarUrl(creator),
    role: 'Member',
    roleTone: isYou ? 'blue' : 'purple',
    isYou,
  };
};

const mapApiTask = (task, currentUserId) => {
  const estimated = task.estimated_hours ?? task.estimated;
  const logged = task.total_hours_logged ?? task.logged_hours ?? task.logged;
  const loggedNum = Number(logged);
  const estimatedNum = Number(estimated);
  const estimatedHours = Number.isFinite(estimatedNum) ? Number(estimatedNum.toFixed(2)) : 0;
  const loggedHours = Number.isFinite(loggedNum) ? Number(loggedNum.toFixed(2)) : 0;
  const status = deriveStatus(task);
  const completed = status === 'Completed';
  const collaborators = mapCollaborators(task);
  const totalCollaborators = Number(task.total_collaborators);
  const collaboratorTotal = Number.isFinite(totalCollaborators)
    ? totalCollaborators
    : collaborators.length;
  const onGoingSessionId = task.on_going_session_id || null;
  const onGoingSessionStatus = task.on_going_session_status || null;
  const onGoingSessionUserStatus = task.on_going_session_user_status || null;
  const sessionAction = resolveTaskSessionAction({
    onGoingSessionId,
    on_going_session_id: onGoingSessionId,
    onGoingSessionStatus,
    on_going_session_status: onGoingSessionStatus,
    onGoingSessionUserStatus,
    on_going_session_user_status: onGoingSessionUserStatus,
  });

  return {
    id: task.id,
    title: task.title || 'Untitled Task',
    status,
    dueDate: formatDate(task.occurrence_date || task.end_date || task.start_date),
    occurrenceDate: (() => {
      const raw = task.occurrence_date || task.end_date || task.start_date || '';
      const match = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
      return match ? `${match[1]}-${match[2]}-${match[3]}` : '';
    })(),
    estimated: formatEstimatedHours(estimated),
    logged: formatLoggedHours(logged),
    collaborators,
    totalCollaborators: collaboratorTotal,
    onGoingSessionId,
    on_going_session_id: onGoingSessionId,
    onGoingSessionStatus,
    on_going_session_status: onGoingSessionStatus,
    onGoingSessionUserStatus,
    on_going_session_user_status: onGoingSessionUserStatus,
    sessionAction: completed ? 'done' : sessionAction,
    action: completed
      ? 'done'
      : sessionAction === 'start'
        ? 'start'
        : 'continue',
    completed,
    description: task.description || '',
    estimated_hours: estimatedHours,
    total_hours_logged: loggedHours,
    total_collaborators: collaboratorTotal,
    timeTracking: {
      total: estimatedHours,
      logged: loggedHours,
      remaining: Math.max(0, Number((estimatedHours - loggedHours).toFixed(2))),
    },
    sessionActivity: [],
    owner: mapTaskOwner(task, currentUserId),
  };
};

/**
 * My Tasks tab — flat personal task list matching Figma 40004314-9104.
 * Tasks Completed badge lives in ProjectLayout tabs-actions (40004381-6012).
 */
const ProjectMyTasksTab = () => {
  const {
    projectId: routeProjectId,
    memberId: routeMemberId,
    taskId: routeTaskId,
  } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { openSessionDetail, isOpen, sessionId, minimize } =
    useSessionDetailPlayer();
  const {
    project,
    taskStats = EMPTY_TASK_STATS,
    fetchMemberSummary: fetchMemberSummaryFromLayout,
    registerTabRefresh,
  } = useOutletContext() || {};
  const projectId = routeProjectId || project?.id || '';
  const myTasksReturnTo = useMemo(
    () => ({
      returnTo: listingReturnToFromPath(location.pathname),
      returnLabel: 'Redirecting to my tasks...',
    }),
    [location.pathname],
  );
  const currentUser = useSelector((state) => state.auth.currentUser);
  const currentUserId = currentUser?.id ? String(currentUser.id) : '';
  const currentUserMemberId = useMemo(
    () => findMemberIdForUser(project, currentUserId),
    [project, currentUserId]
  );
  const memberId = routeMemberId || currentUserMemberId;
  const projectCollaborators = useMemo(
    () =>
      getProjectCollaborators(project, currentUser, {
        excludeCurrentUser: true,
      }),
    [project, currentUser]
  );
  const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [sessionModal, setSessionModal] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [isJoiningSession, setIsJoiningSession] = useState(false);
  const [fetchedTask, setFetchedTask] = useState(null);
  const [memberDetail, setMemberDetail] = useState(null);
  const fetchedTaskIdRef = useRef('');

  const headerOwner = useMemo(
    () => mapMemberDetailToOwner(memberDetail, currentUserId),
    [memberDetail, currentUserId]
  );
  const isOwnTasks = Boolean(
    headerOwner?.isYou ||
      (currentUserMemberId && memberId && memberId === currentUserMemberId)
  );

  const openAddTask = useCallback(() => setIsCreateTaskOpen(true), []);
  const closeAddTask = useCallback(() => setIsCreateTaskOpen(false), []);
  const closeSessionModal = () => {
    setSessionModal(null);
    setActiveTask(null);
  };

  const handleStartSession = (task, type) => {
    if (!task) return;

    const sessionAction = resolveTaskSessionAction(task);
    const ongoingSessionId =
      task.onGoingSessionId || task.on_going_session_id || null;

    if (sessionAction === 'view' && ongoingSessionId) {
      const floatingSessionId = sessionId ? String(sessionId) : '';
      const clickedId = String(ongoingSessionId);
      if (isOpen && floatingSessionId && clickedId !== floatingSessionId) {
        minimize?.();
        navigate(`/projects/${projectId}/sessions/${clickedId}`);
        return;
      }
      openSessionDetail({
        sessionId: ongoingSessionId,
        projectId,
        project,
        ...myTasksReturnTo,
      });
      return;
    }

    setActiveTask(task);
    if (sessionAction === 'continue') {
      setSessionModal('join');
      return;
    }
    setSessionModal(type === 'shadow' ? 'shadow' : 'solo');
  };

  const fetchMemberSummary = fetchMemberSummaryFromLayout;

  const handleStatusChange = async (task, nextLabel) => {
    const apiStatus = STATUS_API_BY_LABEL[nextLabel];
    if (!projectId || !task?.id || !apiStatus || task.status === nextLabel) {
      return;
    }

    const previous = task;
    const nextTask = applyTaskStatus(task, nextLabel);
    setTasks((prev) =>
      prev.map((item) => (item.id === task.id ? nextTask : item))
    );
    setFetchedTask((prev) =>
      prev && String(prev.id) === String(task.id) ? nextTask : prev
    );

    const response = await patch(
      endpoints.updateTaskStatus(projectId, task.id),
      { status: apiStatus }
    );

    if (response?.data?.success) {
      const raw = unwrapTaskPayload(response.data.result);
      if (raw?.status) {
        const label = mapApiStatusLabel(raw.status) || nextLabel;
        const mapped = raw.title
          ? mapApiTask(raw, currentUserId)
          : applyTaskStatus(previous, label);
        setTasks((prev) =>
          prev.map((item) => (item.id === task.id ? mapped : item))
        );
        setFetchedTask((prev) =>
          prev && String(prev.id) === String(task.id) ? mapped : prev
        );
      }
      fetchMemberSummary?.();
      return;
    }

    setTasks((prev) =>
      prev.map((item) => (item.id === task.id ? previous : item))
    );
    setFetchedTask((prev) =>
      prev && String(prev.id) === String(task.id) ? previous : prev
    );
    toast.error(getApiErrorMessage(response, 'Failed to update status'), {
      position: 'bottom-center',
      theme: 'colored',
      transition: Bounce,
    });
  };

  useEffect(() => {
    if (!projectId || routeMemberId || !currentUserMemberId) return;
    navigate(
      routeTaskId
        ? taskDetailPath(projectId, currentUserMemberId, routeTaskId)
        : taskListPath(projectId, currentUserMemberId),
      { replace: true }
    );
  }, [projectId, currentUserMemberId, routeMemberId, routeTaskId, navigate]);

  useEffect(() => {
    if (!memberId) {
      setMemberDetail(null);
      return undefined;
    }

    setMemberDetail(null);
    let cancelled = false;

    const loadMember = async () => {
      const response = await get(endpoints.getProjectMemberById(memberId), {
        params: { 'with[]': ['user'] },
        skipErrorToast: true,
      });

      if (cancelled) return;

      if (response?.data?.success) {
        setMemberDetail(response.data.result || null);
        return;
      }

      setMemberDetail(null);
    };

    loadMember();
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  const selectedFromList = useMemo(
    () =>
      routeTaskId
        ? tasks.find((task) => String(task.id) === String(routeTaskId)) || null
        : null,
    [tasks, routeTaskId]
  );

  const selectedTask = selectedFromList ||
    (fetchedTask && String(fetchedTask.id) === String(routeTaskId)
      ? fetchedTask
      : null);

  const openTaskDetail = useCallback(
    (task) => {
      if (!projectId || !memberId || !task?.id) return;
      navigate(taskDetailPath(projectId, memberId, task.id));
    },
    [navigate, projectId, memberId]
  );

  const closeTaskDetail = useCallback(() => {
    if (!projectId || !memberId) return;
    navigate(taskListPath(projectId, memberId), { replace: true });
  }, [navigate, projectId, memberId]);

  const handleTaskUpdated = useCallback((taskId, patchFields) => {
    const applyPatch = (item) =>
      String(item.id) === String(taskId) ? { ...item, ...patchFields } : item;

    setTasks((prev) => prev.map(applyPatch));
    setFetchedTask((prev) =>
      prev && String(prev.id) === String(taskId) ? applyPatch(prev) : prev
    );
  }, []);

  const fetchTasks = useCallback(async () => {
    if (!projectId || !memberId) {
      setTasks([]);
      setTotalItems(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const [response] = await Promise.all([
      get(endpoints.getTasks, {
        params: {
          project_id: projectId,
          page: currentPage,
          per_page: PAGE_SIZE,
          page_size: PAGE_SIZE,
          'collaborator_ids[]': memberId,
          'with[]': TASK_INCLUDES,
        },
      }),
      fetchMemberSummary?.() || Promise.resolve(),
    ]);

    if (response?.data?.success) {
      const result = response.data.result || {};
      const rows = (result.data || []).map((task) => mapApiTask(task, currentUserId));
      const total = result.total ?? rows.length;
      const perPage = result.per_page || result.page_size || PAGE_SIZE;

      setTasks(rows);
      setTotalItems(total);
      setPageSize(perPage);

      const totalPages = Math.max(1, Math.ceil(total / perPage));
      if (currentPage > totalPages) {
        setCurrentPage(totalPages);
      }
    } else if (response) {
      setTasks([]);
      setTotalItems(0);
      setError(getApiErrorMessage(response, 'Failed to load tasks'));
    }

    setIsLoading(false);
  }, [projectId, memberId, currentUserId, currentPage, fetchMemberSummary]);

  const handleTaskDeleted = useCallback(
    (taskId) => {
      setFetchedTask((prev) =>
        prev && String(prev.id) === String(taskId) ? null : prev
      );
      closeTaskDetail();
      fetchMemberSummary?.();

      const remainingOnPage = tasks.filter(
        (item) => String(item.id) !== String(taskId)
      ).length;
      const newTotal = Math.max(0, totalItems - 1);
      const effectivePageSize = pageSize || PAGE_SIZE;
      const newTotalPages = Math.max(1, Math.ceil(newTotal / effectivePageSize));

      if (currentPage > newTotalPages) {
        setCurrentPage(newTotalPages);
        return;
      }
      if (remainingOnPage === 0 && currentPage > 1) {
        setCurrentPage((page) => page - 1);
        return;
      }
      fetchTasks();
    },
    [
      closeTaskDetail,
      fetchMemberSummary,
      fetchTasks,
      tasks,
      totalItems,
      pageSize,
      currentPage,
    ]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [memberId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    if (typeof registerTabRefresh !== 'function') return undefined;
    return registerTabRefresh(fetchTasks);
  }, [registerTabRefresh, fetchTasks]);

  // Keep Start / Session Joined / Join Session labels in sync after exit.
  useEffect(() => {
    const handleSessionLeft = (event) => {
      const leftSessionId = event?.detail?.sessionId;
      if (!leftSessionId) return;

      setTasks((prev) =>
        prev.map((task) => clearTaskSessionIfMatches(task, leftSessionId))
      );
      setFetchedTask((prev) =>
        prev ? clearTaskSessionIfMatches(prev, leftSessionId) : prev
      );
      setActiveTask((prev) =>
        prev ? clearTaskSessionIfMatches(prev, leftSessionId) : prev
      );
    };

    window.addEventListener('session-left', handleSessionLeft);
    return () => window.removeEventListener('session-left', handleSessionLeft);
  }, []);

  useEffect(() => {
    if (!routeTaskId) {
      fetchedTaskIdRef.current = '';
      setFetchedTask(null);
      return;
    }

    if (selectedFromList) {
      fetchedTaskIdRef.current = String(routeTaskId);
      setFetchedTask(null);
      return;
    }

    if (fetchedTaskIdRef.current === String(routeTaskId)) return;

    let cancelled = false;

    const loadTask = async () => {
      const byId = await get(endpoints.getTaskById(routeTaskId), {
        params: { 'with[]': TASK_INCLUDES },
        skipErrorToast: true,
      });

      let payload = byId?.data?.success
        ? unwrapTaskPayload(byId.data.result)
        : null;

      if (!payload?.id) {
        const listed = await get(endpoints.getTasks, {
          params: {
            project_id: projectId,
            page: 1,
            per_page: 1,
            page_size: 1,
            id: routeTaskId,
            'with[]': TASK_INCLUDES,
          },
          skipErrorToast: true,
        });
        payload = listed?.data?.success
          ? unwrapTaskPayload(listed.data.result)
          : null;
      }

      if (cancelled) return;

      if (payload?.id && String(payload.id) === String(routeTaskId)) {
        fetchedTaskIdRef.current = String(routeTaskId);
        setFetchedTask(mapApiTask(payload, currentUserId));
        return;
      }

      if (isLoading) return;

      fetchedTaskIdRef.current = String(routeTaskId);
      setFetchedTask(null);
      toast.error('Task not found.', {
        position: 'bottom-center',
        theme: 'colored',
        transition: Bounce,
      });
      if (projectId && memberId) {
        navigate(taskListPath(projectId, memberId), { replace: true });
      }
    };

    loadTask();
    return () => {
      cancelled = true;
    };
  }, [
    routeTaskId,
    selectedFromList,
    isLoading,
    projectId,
    memberId,
    currentUserId,
    navigate,
  ]);

  const handleSaveTask = () => {
    closeAddTask();
    fetchTasks();
  };

  const createSession = async ({ type, note, userIds = [], title } = {}) => {
    if (!projectId || isCreatingSession) return null;

    setIsCreatingSession(true);
    try {
      const taskId = activeTask?.id;
      const result = await createProjectSession({
        type,
        projectId,
        note,
        userIds,
        title: taskId
          ? title || activeTask?.title || activeTask?.name
          : undefined,
        sessionTarget: taskId ? 'task' : 'project',
        targetIds: taskId ? [taskId] : [projectId],
      });

      if (!result.ok) {
        if (result.reason === 'empty_note') {
          toast.error('Please add a working note before starting the session.', {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          });
        } else if (result.response) {
          toast.error(result.response?.data?.message || 'Failed to create session', {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          });
        }
        return null;
      }

      toast.success(
        type === 'solo' ? 'Solo session created.' : 'Shadow session created.',
        {
          position: 'bottom-center',
          theme: 'colored',
          transition: Bounce,
        }
      );
      const createdSessionId = result.created?.id || null;
      if (createdSessionId && taskId) {
        const patch = (task) =>
          String(task.id) === String(taskId)
            ? markTaskSessionJoined(task, createdSessionId)
            : task;
        setTasks((prev) => prev.map(patch));
        setFetchedTask((prev) =>
          prev && String(prev.id) === String(taskId) ? patch(prev) : prev
        );
      }
      closeSessionModal();
      if (createdSessionId) {
        openSessionDetail({
          sessionId: createdSessionId,
          projectId,
          project,
          ...myTasksReturnTo,
        });
      }
      return result.created;
    } catch (err) {
      if (err?.response || err?.code) {
        return null;
      }
      toast.error(err?.message || 'Failed to create session', {
        position: 'bottom-center',
        theme: 'colored',
        transition: Bounce,
      });
      return null;
    } finally {
      setIsCreatingSession(false);
    }
  };

  const joinSession = async ({ note } = {}) => {
    if (isJoiningSession) return null;

    const sessionId =
      activeTask?.onGoingSessionId || activeTask?.on_going_session_id;
    if (!sessionId) {
      toast.error('No ongoing session found for this task.', {
        position: 'bottom-center',
        theme: 'colored',
        transition: Bounce,
      });
      return null;
    }

    setIsJoiningSession(true);
    try {
      const result = await joinProjectSession({ sessionId, note });

      if (!result.ok) {
        if (result.reason === 'empty_note') {
          toast.error('Please add a working note before joining the session.', {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          });
        } else if (result.response) {
          toast.error(result.response?.data?.message || 'Failed to join session', {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          });
        }
        return null;
      }

      toast.success('Joined session.', {
        position: 'bottom-center',
        theme: 'colored',
        transition: Bounce,
      });
      const joinedTaskId = activeTask?.id;
      if (joinedTaskId) {
        const patch = (task) =>
          String(task.id) === String(joinedTaskId)
            ? markTaskSessionJoined(task, sessionId)
            : task;
        setTasks((prev) => prev.map(patch));
        setFetchedTask((prev) =>
          prev && String(prev.id) === String(joinedTaskId) ? patch(prev) : prev
        );
      }
      closeSessionModal();
      openSessionDetail({ sessionId, projectId, project, ...myTasksReturnTo });
      return result.joined;
    } catch (err) {
      if (err?.response || err?.code) {
        return null;
      }
      toast.error(err?.message || 'Failed to join session', {
        position: 'bottom-center',
        theme: 'colored',
        transition: Bounce,
      });
      return null;
    } finally {
      setIsJoiningSession(false);
    }
  };

  return (
    <>
      <div className="project-detail__table-wrap">
        <ProjectMyTasksTable
          tasks={tasks}
          isLoading={isLoading}
          error={error}
          playIconSrc={ICONS.play}
          checkIconSrc={ICONS.checkGreen}
          owner={headerOwner}
          stats={taskStats}
          selectedTask={selectedTask}
          onSelectTask={openTaskDetail}
          onCloseTask={closeTaskDetail}
          onAddTask={isOwnTasks ? openAddTask : undefined}
          plusIconSrc={ICONS.plus}
          onStartSession={isOwnTasks ? handleStartSession : undefined}
          onStatusChange={handleStatusChange}
          onCollaboratorsAdded={fetchTasks}
          onTaskUpdated={handleTaskUpdated}
          onTaskDeleted={handleTaskDeleted}
        />
      </div>
      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel="Tasks"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
      />
      <CreateTaskModal
        isOpen={isCreateTaskOpen}
        projectId={projectId}
        project={project}
        projectStartDate={project?.startDate || project?.start_date || ''}
        projectEndDate={project?.endDate || project?.end_date || ''}
        onClose={closeAddTask}
        onSave={handleSaveTask}
      />
      <StartSoloSessionModal
        isOpen={sessionModal === 'solo'}
        onClose={closeSessionModal}
        project={project}
        task={activeTask}
        onStart={({ note, title }) =>
          createSession({ type: 'solo', note, title, userIds: [] })
        }
        isSubmitting={isCreatingSession}
      />
      <StartShadowSessionModal
        isOpen={sessionModal === 'shadow'}
        onClose={closeSessionModal}
        project={project}
        task={activeTask}
        collaborators={projectCollaborators}
        onStart={({ note, userIds, title }) =>
          createSession({ type: 'shadow', note, userIds, title })
        }
        isSubmitting={isCreatingSession}
      />
      <JoinShadowSessionModal
        isOpen={sessionModal === 'join'}
        onClose={closeSessionModal}
        project={project}
        collaborators={activeTask?.collaborators || []}
        onJoin={joinSession}
        isSubmitting={isJoiningSession}
      />
    </>
  );
};

export default ProjectMyTasksTab;
